export type TranscriptEntry = { id: string; text: string; timestamp: string; isLive?: boolean };

export type UserTranscriptTurn = {
  id: number;
  user_transcript: string;
  created_at: string;
  metadata?: Record<string, unknown>;
};

const validTranscriptTimestamp = (value: unknown): value is string => (
  typeof value === 'string' && value.trim() !== '' && Number.isFinite(Date.parse(value))
);

export const buildUserTranscriptEntries = (
  turnHistory: UserTranscriptTurn[],
  liveEntry: TranscriptEntry | null,
): TranscriptEntry[] => {
  const entries = new Map<string, TranscriptEntry>();
  turnHistory.forEach((turn) => {
    if (!turn.user_transcript.trim()) return;
    const captureId = turn.metadata?.live_capture_id ?? turn.metadata?.input_audio_item_id;
    const id = typeof captureId === 'string' && captureId ? `user-capture-${captureId}` : `user-${turn.id}`;
    const startedAt = turn.metadata?.live_capture_started_at ?? turn.metadata?.input_audio_started_at;
    entries.set(id, {
      id,
      text: turn.user_transcript,
      // A saved Live row can arrive after the guide has already replied. Keep
      // the speech's occurrence time, not the time evaluation saved the row.
      timestamp: validTranscriptTimestamp(startedAt) ? startedAt : turn.created_at,
    });
  });
  if (liveEntry?.text.trim()) {
    const id = `user-capture-${liveEntry.id}`;
    const saved = entries.get(id);
    const capturedText = liveEntry.text.trim();
    const savedText = saved?.text.trim() ?? '';
    entries.set(id, {
      ...liveEntry,
      id,
      // Polling may have received a newer snapshot before the active caption.
      // Reconcile only the same capture; identical independent speech stays.
      text: saved && savedText.length > capturedText.length && savedText.startsWith(capturedText)
        ? saved.text : liveEntry.text,
      timestamp: validTranscriptTimestamp(liveEntry.timestamp)
        ? liveEntry.timestamp : saved?.timestamp ?? liveEntry.timestamp,
      isLive: true,
    });
  }
  return [...entries.values()];
};
